import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import {
  Calendar,
  User,
  Building2,
  AlertCircle,
  MessageSquare,
  Clock,
} from 'lucide-react'
import type { TareaCompleta, EstadoTarea } from '@/types/database'
import { format, parseISO, isPast, isToday, formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'

interface TareaPreviewContentProps {
  tarea: TareaCompleta
}

const ESTADO_STYLES: Record<EstadoTarea, string> = {
  pendiente: 'bg-slate-100 text-slate-800',
  en_proceso: 'bg-blue-100 text-blue-800',
  completada: 'bg-green-100 text-green-800',
}

const ESTADO_LABELS: Record<EstadoTarea, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  completada: 'Completada',
}

export function TareaPreviewContent({ tarea }: TareaPreviewContentProps) {
  const fechaLimite = tarea.fecha_limite ? parseISO(tarea.fecha_limite) : null
  const estaVencida = fechaLimite && isPast(fechaLimite) && tarea.estado !== 'completada'
  const esHoy = fechaLimite && isToday(fechaLimite)
  const fechaCreacion = tarea.created_at ? parseISO(tarea.created_at) : new Date()

  const resolveNombre = useUserNameResolver()
  const estado = (tarea.estado as EstadoTarea | null) ?? 'pendiente'

  return (
    <div className="space-y-3">
      {/* Header con badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {tarea.prioridad === 'urgente' && (
          <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-200">
            Urgente
          </Badge>
        )}
        <Badge className={cn('text-xs', ESTADO_STYLES[estado])}>
          {ESTADO_LABELS[estado]}
        </Badge>
      </div>

      {/* Título */}
      <p className="font-medium text-sm leading-tight">{tarea.titulo}</p>

      {/* Descripción */}
      {tarea.descripcion && (
        <p className="text-xs text-muted-foreground line-clamp-3">
          {tarea.descripcion}
        </p>
      )}

      <Separator />

      {/* Metadata */}
      <div className="grid grid-cols-1 gap-2 text-xs">
        {/* Cliente */}
        {tarea.cliente_nombre && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{tarea.cliente_nombre}</span>
          </div>
        )}

        {/* Vencimiento fiscal */}
        {tarea.tipo_impuesto && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <AlertCircle className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {tarea.tipo_impuesto} - {tarea.vencimiento_periodo}
            </span>
          </div>
        )}

        {/* Asignado */}
        {tarea.asignado_a && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="h-3 w-3 shrink-0" />
            <span>Asignado a {resolveNombre(tarea.asignado_a)}</span>
          </div>
        )}

        {/* Fecha límite */}
        {fechaLimite && (
          <div
            className={cn(
              'flex items-center gap-2',
              estaVencida
                ? 'text-red-600'
                : esHoy
                  ? 'text-orange-600'
                  : 'text-muted-foreground'
            )}
          >
            <Calendar className="h-3 w-3 shrink-0" />
            <span>
              {estaVencida
                ? `Vencida hace ${formatDistanceToNow(fechaLimite, { locale: es })}`
                : esHoy
                  ? 'Vence hoy'
                  : `Vence el ${format(fechaLimite, "d 'de' MMMM", { locale: es })}`}
            </span>
          </div>
        )}

        {/* Comentarios */}
        {(tarea.cantidad_comentarios ?? 0) > 0 && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MessageSquare className="h-3 w-3 shrink-0" />
            <span>
              {tarea.cantidad_comentarios}{' '}
              {tarea.cantidad_comentarios === 1 ? 'comentario' : 'comentarios'}
            </span>
          </div>
        )}

        {/* Creado */}
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-3 w-3 shrink-0" />
          <span>Creada {formatDistanceToNow(fechaCreacion, { locale: es, addSuffix: true })}</span>
        </div>
      </div>

      {/* Footer hint */}
      <p className="text-[10px] text-muted-foreground/70 text-center pt-1">
        Clic para ver detalles completos
      </p>
    </div>
  )
}
